import { CloudDone } from '@mui/icons-material';
import { Box, CircularProgress, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import type { JSONContent } from '@tiptap/core';
import {
  isTouchDevice,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonEditLink,
  MenuButtonHighlightToggle,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonStrikethrough,
  MenuButtonTaskList,
  MenuButtonUnderline,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditor,
  type RichTextEditorRef,
} from 'mui-tiptap';
import { useEffect, useRef } from 'react';
import { tiptapEditorExtensions, tiptapStyles } from '@/utils/server/tiptap';

const containerStyles = {
  borderRadius: 0,
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  cursor: 'text',
  '& .MuiTiptap-FieldContainer-root': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflowY: 'auto',
    borderRadius: 0,
  },
  '& .MuiTiptap-FieldContainer-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.23)',
  },
  '& .MuiTiptap-FieldContainer-root:hover .MuiTiptap-FieldContainer-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  '& .MuiTiptap-FieldContainer-focused .MuiTiptap-FieldContainer-notchedOutline': {
    borderColor: 'common.white',
  },
  '& .MuiTiptap-RichTextContent-root': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  '& .MuiTiptap-RichTextContent-root .tiptap.ProseMirror': {
    flex: 1,
    minHeight: '100%',
  },
};

export default function TextEditor({
  content,
  contentId,
  isLoading,
  isSuccess,
  onUpdate,
}: {
  content?: JSONContent;
  contentId: string;
  isLoading?: boolean;
  isSuccess?: boolean;
  onUpdate?: (id: string, note?: JSONContent) => void;
}) {
  const rteRef = useRef<RichTextEditorRef>(null);
  const theme = useTheme();

  const onUpdateNote = () => {
    if (onUpdate) onUpdate(contentId, rteRef.current?.editor?.getJSON());
  };

  const focusEditor = () => {
    rteRef.current?.editor?.commands.focus('end');
  };

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('.MuiTiptap-MenuBar-root') || target.closest('button')) {
      return;
    }
    focusEditor();
  };

  useEffect(() => {
    const editor = rteRef.current?.editor;
    if (editor) {
      if (content) {
        queueMicrotask(() => {
          editor.chain().setContent(content).run();
        });
      } else {
        queueMicrotask(() => {
          editor.chain().clearContent().run();
        });
      }
    }
  }, [content]);

  return (
    <Box
      sx={{ ...containerStyles, ...tiptapStyles(theme) } as SxProps<Theme>}
      onClick={handleContainerClick}>
      <RichTextEditor
        ref={rteRef}
        onUpdate={onUpdateNote}
        extensions={tiptapEditorExtensions}
        content={content}
        immediatelyRender={false}
        RichTextFieldProps={{
          variant: 'outlined',
          sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderRadius: 0 },
        }}
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        renderControls={() => (
          <MenuControlsContainer>
            <MenuSelectHeading />
            <MenuDivider />
            <MenuButtonBold />
            <MenuButtonItalic />
            <MenuButtonStrikethrough />
            <MenuButtonUnderline />
            <MenuButtonHighlightToggle />
            <MenuButtonCode />
            <MenuButtonEditLink />
            {isTouchDevice() && (
              <>
                <MenuButtonIndent />
                <MenuButtonUnindent />
              </>
            )}
            <MenuDivider />
            <MenuButtonBulletedList />
            <MenuButtonOrderedList />
            <MenuButtonTaskList />
            {/* Display loading spinner or success icon */}
            {!isSuccess && isLoading && <CircularProgress size={24} sx={{ ml: 'auto' }} />}
            {isSuccess && !isLoading && <CloudDone sx={{ ml: 'auto' }} color="success" />}
          </MenuControlsContainer>
        )}
      />
    </Box>
  );
}
